// Performance optimization utilities for better user experience

/**
 * Preload a component dynamically for better UX
 * @param {Function} importFunction - The dynamic import function
 * @returns {Promise} The import promise
 */
export const preloadComponent = (importFunction) => {
  const componentImport = importFunction();
  
  // Cache the import for faster subsequent loads
  if (typeof componentImport.then === 'function') {
    componentImport.catch(() => {
      // Ignore preload failures, component will still load when needed
    });
  }
  
  return componentImport;
};

/**
 * Create a function to preload routes based on user navigation patterns
 * @param {Object} routes - Object containing route import functions
 */
export const createRoutePreloader = (routes) => {
  const preloadedRoutes = new Set();
  
  return {
    preload: (routeName) => {
      if (!preloadedRoutes.has(routeName) && routes[routeName]) {
        preloadedRoutes.add(routeName);
        preloadComponent(routes[routeName]);
      }
    },
    
    preloadMultiple: (routeNames) => {
      routeNames.forEach(routeName => {
        if (!preloadedRoutes.has(routeName) && routes[routeName]) {
          preloadedRoutes.add(routeName);
          // Add small delays to avoid blocking the main thread
          setTimeout(() => preloadComponent(routes[routeName]), Math.random() * 100);
        }
      });
    },
    
    preloadCommon: () => {
      // Preload commonly accessed routes
      const commonRoutes = ['packages', 'automations', 'users', 'settings'];
      commonRoutes.forEach((routeName, index) => {
        setTimeout(() => {
          if (routes[routeName] && !preloadedRoutes.has(routeName)) {
            preloadedRoutes.add(routeName);
            preloadComponent(routes[routeName]);
          }
        }, index * 200); // Stagger preloads
      });
    }
  };
};

/**
 * Optimize images by lazy loading and WebP support
 * @param {string} src - Image source
 * @param {Object} options - Optimization options
 */
export const optimizeImage = (src, options = {}) => {
  const { 
    lazy = true, 
    webp = true, 
    placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB2aWV3Qm94PSIwIDAgMSAxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSIxIiBmaWxsPSIjRjVGNUY1Ii8+Cjwvc3ZnPgo=' 
  } = options;
  
  // Check for WebP support
  const supportsWebP = (() => {
    try {
      return document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) {
      return false;
    }
  })();
  
  let optimizedSrc = src;
  
  if (webp && supportsWebP && src && !src.includes('.svg')) {
    optimizedSrc = src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  }
  
  return {
    src: optimizedSrc,
    loading: lazy ? 'lazy' : 'eager',
    placeholder: lazy ? placeholder : undefined
  };
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately
 */
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 */
export const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function(...args) {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

/**
 * Virtual scrolling helper for large lists
 * @param {Array} items - Array of items
 * @param {number} itemHeight - Height of each item
 * @param {number} containerHeight - Height of container
 * @param {number} scrollTop - Current scroll position
 */
export const getVirtualScrollItems = (items, itemHeight, containerHeight, scrollTop) => {
  const totalItems = items.length;
  const visibleItemsCount = Math.ceil(containerHeight / itemHeight);
  const scrollOffset = Math.floor(scrollTop / itemHeight);
  
  const startIndex = Math.max(0, scrollOffset - 5); // Buffer items
  const endIndex = Math.min(totalItems, scrollOffset + visibleItemsCount + 5);
  
  const visibleItems = items.slice(startIndex, endIndex).map((item, index) => ({
    ...item,
    index: startIndex + index,
    top: (startIndex + index) * itemHeight
  }));
  
  return {
    visibleItems,
    totalHeight: totalItems * itemHeight,
    startIndex,
    endIndex
  };
};

/**
 * Memory cleanup utilities
 */
export const memoryCleanup = {
  // Clean up event listeners
  removeEventListener: (element, event, handler) => {
    if (element && element.removeEventListener) {
      element.removeEventListener(event, handler);
    }
  },
  
  // Clean up intervals
  clearIntervals: (intervals) => {
    intervals.forEach(interval => {
      if (interval) clearInterval(interval);
    });
  },
  
  // Clean up timeouts
  clearTimeouts: (timeouts) => {
    timeouts.forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
  }
};

/**
 * Web Worker helper for offloading heavy computations
 * @param {Function} workerFunction - Function to run in worker
 * @param {any} data - Data to pass to worker
 */
export const createWebWorker = (workerFunction, data) => {
  return new Promise((resolve, reject) => {
    const workerBlob = new Blob([`
      self.onmessage = function(e) {
        const result = (${workerFunction.toString()})(e.data);
        self.postMessage(result);
      }
    `], { type: 'application/javascript' });
    
    const worker = new Worker(URL.createObjectURL(workerBlob));
    
    worker.onmessage = (e) => {
      resolve(e.data);
      worker.terminate();
      URL.revokeObjectURL(workerBlob);
    };
    
    worker.onerror = (error) => {
      reject(error);
      worker.terminate();
      URL.revokeObjectURL(workerBlob);
    };
    
    worker.postMessage(data);
  });
};

// Route definitions for preloading
export const routeImports = {
  packages: () => import('../pages/PackagesPage'),
  automations: () => import('../pages/AutomationsPage'),
  users: () => import('../pages/UsersPage'),
  settings: () => import('../pages/SettingsPage'),
  executors: () => import('../pages/ExecutorsPage'),
  schedules: () => import('../pages/SchedulesPage'),
  queues: () => import('../pages/QueuesPage'),
  organization: () => import('../pages/OrganizationPage'),
  credentials: () => import('../pages/CredentialsPage'),
  environments: () => import('../pages/EnvironmentPage'),
  inputFiles: () => import('../pages/InputFilesPage'),
  createJob: () => import('../pages/CreateJobPage'),
  createSchedule: () => import('../pages/CreateSchedulePage'),
  uploadPackage: () => import('../pages/UploadPackagePage'),
  registerExecutor: () => import('../pages/RegisterExecutorPage')
};

// Create the route preloader instance
export const routePreloader = createRoutePreloader(routeImports);